import { Injectable } from '@angular/core';
import * as cryptoJs from 'crypto-js';

@Injectable({ providedIn: 'root' })
export class CryptoService {
  private readonly algorithm = 'AES-GCM';
  private readonly keyLength = 256;

  constructor() {}

  computeSHA256Hash(value: string, salt: string): string {
    return cryptoJs.SHA256(`${value}${salt}`).toString();
  }

  verifyComputedHash(hash: string, value: string, salt: string): boolean {
    const newHash = this.computeSHA256Hash(value, salt);
    for (let i = 0; i < hash.length; i++) {
      if (newHash[i] !== hash[i]) {
        return false;
      }
    }
    return true;
  }

  async generateKey(secret: string, salt: Uint8Array<ArrayBuffer>): Promise<CryptoKey> {
    const enc = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
      'raw', enc.encode(secret), 'PBKDF2', false, ['deriveKey']
    );
    return crypto.subtle.deriveKey(
      { name: 'PBKDF2', salt, iterations: 100000, hash: 'SHA-256' },
      keyMaterial,
      { name: this.algorithm, length: this.keyLength },
      false,
      ['encrypt', 'decrypt']
    );
  }

  async encrypt(plaintext: string, secret: string): Promise<string> {
    const enc = new TextEncoder();
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const key = await this.generateKey(secret, salt);

    const encrypted = await crypto.subtle.encrypt(
      { name: this.algorithm, iv },
      key,
      enc.encode(plaintext)
    );

    // Pack: salt(16) + iv(12) + ciphertext+tag, then base64
    const combined = new Uint8Array(salt.length + iv.length + encrypted.byteLength);
    combined.set(salt, 0);
    combined.set(iv, salt.length);
    combined.set(new Uint8Array(encrypted), salt.length + iv.length);

    return btoa(String.fromCharCode(...combined));
  }
}
